import { format } from "date-fns";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useAuth } from "contexts/AuthContext";
import { useChatHandle } from "contexts/ChatHandleContext";
import { uuid } from "lib/uid";
import { compact, entries, isArray, isEmpty, noop } from "lodash";
import { EMPTY_OBJECT } from "constants/defaults";
import { useEffect, useState } from "react";
import { RADIO_STATES } from "components/Common/Radio";
import { getConditionalString } from "lib/getConditionalValue";
import { CHAT_ENTRY_TYPES } from "../chatConsts";

// const separator = "\n\n---------------\n---------------\n\n";

// const splitAndGetRest = (str, val) => {
//   const [prev, ...rest] = str.split(val);

//   return [prev, rest.join(val)];
// };
// const parseChatEntry = (entry) => {
//   let [id, rest] = splitAndGetRest(entry.substr(1), "]");
//   const [dateStr, restRest] = splitAndGetRest(rest.substr(1), " GMT - ");
//   const [user, content] = splitAndGetRest(restRest, ":\n");

//   return { id, dateStr, user, content };
// };
// const buildChatEntry = (entry) => {
//   // console.log(entry);
//   let dateAdded;
//   try {
//     dateAdded = format(new Date(entry.dateAdded), "HH:mm:ss", {
//       timeZone: "GMT",
//     });
//   } catch (err) {
//     dateAdded = "99:99:99";
//   }
//   const userName = entry.user || "Unknown User";

//   return `[${entry.id}]\n${dateAdded} GMT - ${userName}:\n${entry.content}`;
// };

const FILE_NAME_PREFIX = "Chat Log: ";

export const useChatLogger = ({ entries, onSync = noop }) => {
  const { user } = useAuth();
  const { groupDoc: rawGroupDoc } = useCurrentGroup();
  const groupDoc = rawGroupDoc || EMPTY_OBJECT;
  const groupId = groupDoc?.id;
  const { chatHandle } = useChatHandle();
  const [handleHasPermission, setHandleHasPermission] = useState(false);

  useEffect(() => {
    if (chatHandle && groupId && user?.id) {
      setHandleHasPermission(true);
    } else {
      setHandleHasPermission(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, chatHandle]);

  useEffect(() => {
    if (!entries || !groupId || !chatHandle || !handleHasPermission) {
      return;
    }

    const writeEntriesToFile = async () => {
      try {
        // Group entries by GMT day
        const entriesByDay = entries.reduce((acc, entry) => {
          const gmtDate = format(new Date(entry.dateAdded), "yyyy-MM-dd", {
            timeZone: "GMT",
          });
          if (!acc[gmtDate]) {
            acc[gmtDate] = [];
          }
          acc[gmtDate].push(entry);
          return acc;
        }, {});

        // For each day's entries, update or create corresponding file
        for (const [date, dayEntries] of Object.entries(entriesByDay)) {
          // const fileName = `${groupDoc?.name} Chat Log - ${date} - (GID_${groupDoc?.id}).txt`;
          const fileName = `${FILE_NAME_PREFIX}${groupId}||${date}`;

          // Format entries for the file
          const entryIdsSet = new Set(dayEntries.map((entry) => entry.id));
          const dayTextEntries = dayEntries.map((entry) => ({
            id: entry.id,
            dateAdded: entry.dateAdded,
            entry,
            // entry.type === CHAT_ENTRY_TYPES.TEXT
            //   ? entry.value
            //   : `[${entry.type}]${JSON.stringify(entry.value)}`,
          }));

          const fileHandle = await chatHandle.getFileHandle(fileName, {
            create: true,
          });
          const file = await fileHandle.getFile();
          const fileText = await file.text();
          // const existingEntries = compact(fileText.split(separator));
          let existingEntries;
          try {
            existingEntries = JSON.parse(fileText).entries;
          } catch (err) {
            console.log(err);
            existingEntries = [];
          }

          const existingTextEntries = existingEntries
            // .map(parseChatEntry)
            .filter((entry) => !entryIdsSet.has(entry.id));

          // console.log({ fileText, existingTextEntries, dayTextEntries });
          const finalTextEntries = [
            ...existingTextEntries,
            ...dayTextEntries,
          ].sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));

          // const formattedContent = finalTextEntries
          //   .map(buildChatEntry)
          //   .join(separator);
          const formattedContent = JSON.stringify({
            name: groupDoc?.name,
            entries: finalTextEntries,
          });

          // Write the content
          const writable = await fileHandle.createWritable();
          await writable.write(formattedContent);
          await writable.close();
        }
        onSync(uuid());
      } catch (error) {
        console.error("Error writing to files:", error);
      }
    };

    writeEntriesToFile();
  }, [
    entries,
    groupId,
    groupDoc?.name,
    chatHandle,
    groupDoc?.id,
    handleHasPermission,
    onSync,
  ]);
};

export const useChatLogReader = () => {
  const { chatHandle } = useChatHandle();

  const getChatLogOptions = async () => {
    const res = {};
    for await (const [key, value] of chatHandle.entries()) {
      try {
        if (key.indexOf(FILE_NAME_PREFIX) === -1) {
          continue;
        }

        const [, rest] = key.split(FILE_NAME_PREFIX);
        const [groupId, date] = rest.split("||");

        if (!groupId || !date) {
          continue;
        }

        const file = await value.getFile();
        const fileText = await file.text();

        const parsed = JSON.parse(fileText);

        res[groupId] = {
          ...(res[groupId] || {}),
          [date]: parsed,
        };
      } catch (err) {
        console.log(err);
        throw new Error(`Failed to parse chat logs: ${err.message}`);
      }
    }

    const test = entries(res).map(([gid, val]) => {
      let groupName;
      const parsedEntries = entries(val)
        .sort((a, b) => (a[0] > b[0] ? -1 : 1))
        .reduce((acc, [, { name, entries } = {}], i) => {
          if (i === 0) {
            groupName = name;
          }

          return [...acc, ...entries];
        }, []);

      return {
        id: gid,
        label: `${groupName || "Unknown Group"}`,
        value: parsedEntries,
      };
    });

    return test;
  };

  const clearChatLogs = async () => {
    try {
      for await (const [key] of chatHandle.entries()) {
        await chatHandle.removeEntry(key, { recursive: true });
      }
    } catch (err) {
      console.error(err);
      alert(err);
    }
  };

  return { getChatLogOptions, clearChatLogs };
};

const convertTrackValueToString = (v) => {
  if (!isArray(v) || isEmpty(v)) {
    return "";
  }

  const mapped = v.map((radio) => {
    switch (radio) {
      case RADIO_STATES.BURNED:
        return "X";
      case RADIO_STATES.MARKED:
        return "⦿";
      case RADIO_STATES.EMPTY:
      default:
        return "○";
    }
  });

  return mapped.join("-");
};
const toFrontOffset = (rawNum) => {
  let num = "" + rawNum;

  if (num.length >= 2) {
    return num;
  }

  return "0" + num;
};
export const parseChatEntryToOutput = (v) => {
  const { entry, dateAdded } = v || {};
  const { name, user, type, value, cut } = entry || {};

  let body;

  if (type === CHAT_ENTRY_TYPES.TEXT) {
    body = entry.value;
  } else if (type === CHAT_ENTRY_TYPES.LIST_ITEM) {
    const { name, details, value: track } = value || {};

    body = compact([
      getConditionalString(name, "", ""),
      getConditionalString(convertTrackValueToString(track), "(", ")"),
      getConditionalString(
        details?.trim(),
        "+++++++++++++++++++++\n",
        "\n+++++++++++++++++++++"
      ) || "",
    ]).join("\n");
  } else if (type === CHAT_ENTRY_TYPES.IMAGE) {
    const src = value?.src || JSON.stringify(value);
    body = `IMAGE: ${src.indexOf("/") === 0 ? `${window.location.origin}${src}` : src}`;
  } else if (type === CHAT_ENTRY_TYPES.DICE_ROLL) {
    body = `DICE ROLL: ${value}${cut ? ` w/ cut of ${cut}` : ""}`;
  } else {
    body = `${type}: ${JSON.stringify(value)}`;
  }

  const date = new Date(dateAdded);

  return `[${name || ""} (${user}) ${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()} ${toFrontOffset(date.getUTCHours())}:${toFrontOffset(date.getUTCMinutes())} UTC]${getConditionalString(body, "\n\n", "")}`;
};
